/**
 * Status configuration utilities for WebhookStatusIndicator
 */

import {
  AlertCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from 'lucide-react';

import type { GitlabWebhook } from '@/lib/types/gitlab';

export const getNoWebhookConfig = (): {
  status: 'none';
  label: string;
  color: 'secondary';
  icon: typeof AlertCircleIcon;
  description: string;
} => ({
  status: 'none' as const,
  label: 'No Webhook',
  color: 'secondary' as const,
  icon: AlertCircleIcon,
  description: 'No webhook configured for this repository',
});

export const getWebhookStatusConfig = (
  status: string,
): {
  status: 'active' | 'pending' | 'error' | 'unknown';
  label: string;
  color: 'default' | 'secondary' | 'destructive';
  icon:
    | typeof CheckCircleIcon
    | typeof ClockIcon
    | typeof XCircleIcon
    | typeof AlertCircleIcon;
  description: string;
} => {
  switch (status) {
    case 'active':
      return {
        status: 'active' as const,
        label: 'Active',
        color: 'default' as const,
        icon: CheckCircleIcon,
        description: 'Webhook is active and receiving events',
      };
    case 'pending':
      return {
        status: 'pending' as const,
        label: 'Pending',
        color: 'secondary' as const,
        icon: ClockIcon,
        description: 'Webhook is being set up',
      };
    case 'error':
      return {
        status: 'error' as const,
        label: 'Error',
        color: 'destructive' as const,
        icon: XCircleIcon,
        description: 'Webhook has encountered an error',
      };
    default:
      return {
        status: 'unknown' as const,
        label: 'Unknown',
        color: 'secondary' as const,
        icon: AlertCircleIcon,
        description: 'Webhook status is unknown',
      };
  }
};

export const getStatusConfig = (
  webhook: GitlabWebhook | null,
):
  | ReturnType<typeof getNoWebhookConfig>
  | ReturnType<typeof getWebhookStatusConfig> => {
  if (!webhook) {
    return getNoWebhookConfig();
  }

  return getWebhookStatusConfig(webhook.status);
};
